<template>
  <div class="community-producer-controls">
    <div class="control-row">
      <label class="control-label">Autor da publicação</label>
      <div v-if="loadingProfiles" class="control-hint">
        Carregando perfis...
      </div>
      <template v-else>
        <b-form-select
          v-model="profileCode"
          class="control-select"
          :options="profileOptions"
        />
        <button type="button" class="btn-link-action" @click="toggleCreate">
          {{ creating ? "Cancelar novo perfil" : "Criar novo perfil" }}
        </button>
      </template>

      <div v-if="creating" class="new-profile mt-2">
        <input
          v-model="newProfileName"
          class="form-control control-input"
          placeholder="Nome do perfil"
        />
        <div class="d-flex align-items-center justify-content-between mt-2">
          <input-image
            :id="`producer-profile-photo-${uid}`"
            v-model="newProfilePhoto"
          >
            <span class="btn-attach">
              <i class="icon-image" /> Foto do perfil
            </span>
          </input-image>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="savingProfile || !newProfileName.trim()"
            @click="saveProfile"
          >
            <loading :show="savingProfile" small>Salvar perfil</loading>
          </button>
        </div>
        <div v-if="newProfilePhoto" class="profile-photo-preview mt-2">
          <img :src="newProfilePhoto" alt="" />
        </div>
      </div>
    </div>

    <div class="control-row">
      <label class="control-label">Agendar publicação</label>
      <div class="d-flex align-items-center">
        <input
          v-model="localSchedule"
          type="datetime-local"
          class="form-control control-input schedule-input"
        />
        <button
          v-if="localSchedule"
          type="button"
          class="btn-link-action ml-2"
          @click="localSchedule = ''"
        >
          Limpar
        </button>
      </div>
      <div class="control-hint">
        Sem data, a publicação é exibida imediatamente.
      </div>
    </div>

    <div class="control-row">
      <b-form-checkbox v-model="pinned">Fixar no topo</b-form-checkbox>
    </div>
  </div>
</template>

<script>
import InputImage from "@/components/InputImage.vue";

let counter = 0;

export default {
  name: "PostProducerControls",
  components: { InputImage },
  props: {
    publishAt: {
      type: String,
      default: null
    },
    isPinned: {
      type: Boolean,
      default: false
    },
    moderatorProfileCode: {
      type: String,
      default: null
    }
  },
  data() {
    counter += 1;
    return {
      uid: counter,
      profiles: [],
      loadingProfiles: false,
      creating: false,
      newProfileName: "",
      newProfilePhoto: null,
      savingProfile: false
    };
  },
  computed: {
    profileOptions() {
      return [
        { value: "", text: "Você (perfil pessoal)" },
        ...this.profiles.map(profile => ({
          value: profile.code,
          text: profile.name
        }))
      ];
    },
    profileCode: {
      get() {
        return this.moderatorProfileCode || "";
      },
      set(value) {
        this.$emit("update:moderatorProfileCode", value || null);
      }
    },
    pinned: {
      get() {
        return this.isPinned;
      },
      set(value) {
        this.$emit("update:isPinned", value);
      }
    },
    localSchedule: {
      get() {
        return this.isoToLocalInput(this.publishAt);
      },
      set(value) {
        this.$emit("update:publishAt", this.localInputToIso(value));
      }
    }
  },
  mounted() {
    this.loadProfiles();
  },
  methods: {
    loadProfiles() {
      this.loadingProfiles = true;
      this.$store
        .dispatch("community/listModeratorProfiles")
        .then(profiles => {
          this.profiles = Array.isArray(profiles) ? profiles : [];
        })
        .catch(() => {
          this.profiles = [];
        })
        .finally(() => {
          this.loadingProfiles = false;
        });
    },
    toggleCreate() {
      this.creating = !this.creating;
      if (!this.creating) {
        this.newProfileName = "";
        this.newProfilePhoto = null;
      }
    },
    saveProfile() {
      if (!this.newProfileName.trim()) return;
      this.savingProfile = true;
      this.$store
        .dispatch("community/createModeratorProfile", {
          name: this.newProfileName.trim(),
          photo: this.newProfilePhoto
        })
        .then(profile => {
          this.profiles = [...this.profiles, profile];
          this.profileCode = profile.code;
          this.creating = false;
          this.newProfileName = "";
          this.newProfilePhoto = null;
          this.$message.success("Perfil criado");
        })
        .catch(error => {
          const message =
            error?.response?.data?.name?.[0] ||
            error?.response?.data?.non_field_errors?.[0] ||
            "Não foi possível criar o perfil";
          this.$message.error(message);
        })
        .finally(() => {
          this.savingProfile = false;
        });
    },
    isoToLocalInput(iso) {
      if (!iso) return "";
      const date = new Date(iso);
      if (isNaN(date.getTime())) return "";
      const pad = value => String(value).padStart(2, "0");
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
          date.getDate()
        )}` + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
      );
    },
    localInputToIso(value) {
      if (!value) return null;
      const date = new Date(value);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    }
  }
};
</script>

<style lang="scss" scoped>
.community-producer-controls {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(128, 128, 128, 0.16);

  .control-row {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .control-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 6px;
  }

  .control-hint {
    font-size: 12px;
    opacity: 0.7;
    margin-top: 4px;
  }

  .control-select,
  .control-input {
    border-radius: 10px;
    font-size: 14px;
  }

  .schedule-input {
    max-width: 240px;
  }

  .btn-link-action {
    border: none;
    background: transparent;
    padding: 0;
    margin-top: 6px;
    color: inherit;
    font-size: 13px;
    opacity: 0.85;
  }

  .btn-attach {
    display: inline-flex;
    align-items: center;
    font-size: 14px;

    i {
      font-size: 18px;
      margin-right: 6px;
    }
  }

  .profile-photo-preview {
    img {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }
  }
}
</style>
